/**
 * Represents a single row in the Audit Detail Table
 * Calculation logic lives here
 */
export class AuditDetailItem {
    constructor(
        public id: string,
        public gameId: string,
        public gameName: string,
        public plu: string,
        public costPerTicket: number,
        public packId: string,
        public startingTicket: number,
        public endingTicket: number | null,
        public qtySold: number,
        public posQty: number,
        public posSales: number,
        public quantityVariance: number,
        public revenueVariance: number,
        public isNewlyActivated: boolean,
        public startingTicketDisplay?: string,
        public endingTicketDisplay?: string,
        public isManualSoldOut: boolean = false,
        public validationMessage?: string
    ) { }

    get inventorySoldAmount(): number {
        return this.qtySold * this.costPerTicket;
    }

    get hasVariance(): boolean {
        return this.quantityVariance !== 0 || this.revenueVariance !== 0;
    }
}

export interface MovedToBackstockItem {
    id: string;
    gameId: string;
    gameName: string;
    plu: string;
    costPerTicket: number;
    packId: string;
    ticketNumberAtMove: number;
    ticketsRemaining: number;
    movedAt?: string;
    fromAuditId?: string;
    toAuditId?: string;
    moved_to_backstock_range: string;
    moved_to_backstock_count: number;
    moved_to_backstock_value: number;
}

export interface AuditShiftActivityItem {
    event_id?: string;
    activity_id?: string;
    event_type?: string;
    slot_id?: number;
    game_id?: number | string;
    pack_id?: number | string | null;
    removed_type?: string | null;
    shift_date?: string | null;
    shift_time?: string | null;
    created_at?: string | null;
    created_by?: string | null;
    created_by_name?: string | null;
    operator_name?: string | null;
    barcode_format?: string | null;
}

export interface AuditNoteEntry {
    id?: string;
    type?: string;
    source?: string;
    text?: string;
    created_at?: string;
    created_at_local?: string;
    store_timezone?: string | null;
    user_id?: string;
    store_user_id?: string;
    author_name?: string;
}

/**
 * Main model for the Audit Detail API response
 */
export class AuditDetailModel {
    private _serverData: any;
    private _items: AuditDetailItem[] | null = null;
    private _movedToBackstockItems: MovedToBackstockItem[] | null = null;

    constructor(data: any) {
        this._serverData = data;
    }

    private _parseNum(val: any): number {
        if (typeof val === 'string') return parseFloat(val) || 0;
        return val ?? 0;
    }

    // Individual Getters following AuditModel pattern
    get auditId() { return this._serverData?.audit?.audit_id; }
    get auditDate() { return this._serverData?.audit?.audit_date; }
    get operatorName() { return this._serverData?.audit?.user_name; }
    get hasVideo(): boolean { return Boolean(this._serverData?.audit?.has_video); }
    get videoUrl(): string | null { return this._serverData?.audit?.video_url ?? null; }
    get notes(): { entries: AuditNoteEntry[] } {
        const notes = this._serverData?.audit?.notes;
        const entries = Array.isArray(notes?.entries) ? notes.entries : [];
        return { entries };
    }
    get noteEntries(): AuditNoteEntry[] { return this.notes.entries; }
    get hasNotes(): boolean { return Boolean(this._serverData?.audit?.has_notes) || this.noteEntries.length > 0; }

    get posTotalScratcherCount() {
        return this._parseNum(this._serverData?.audit_totals?.pos?.scratcher_count);
    }

    get posTotalScratcherSales() {
        return this._parseNum(this._serverData?.audit_totals?.pos?.scratcher_sales);
    }

    get shiftActivity(): AuditShiftActivityItem[] {
        const activity = this._serverData?.audit?.shift_activity || this._serverData?.shift_activity;
        if (!Array.isArray(activity)) {
            return [];
        }

        return activity.map((item: any) => ({
            ...item,
            operator_name: item?.operator_name || item?.created_by_name || null,
        }));
    }

    get scratcherCountVariance() {
        return this._parseNum(this._serverData?.audit_totals?.reconciliation_variance?.scratcher?.quantity_variance);
    }

    get scratcherSalesVariance() {
        return this._parseNum(this._serverData?.audit_totals?.reconciliation_variance?.scratcher?.revenue_variance);
    }

    // Individual Getters - Audit Object
    get auditTotalSold(): string | number {
        return this._serverData?.audit_totals?.audit?.total_sold ?? 0;
    }

    get auditTotalRevenue(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.total_revenue);
    }

    get auditTotalPacks(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.total_packs);
    }

    get auditTotalGames(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.total_games);
    }

    get auditNewlyActivatedCount(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.newly_activated_count);
    }

    get auditNewlyActivatedValue(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.newly_activated_value);
    }

    get auditOpeningCount(): string | number {
        return this._serverData?.audit_totals?.audit?.opening_count ?? 0;
    }

    get auditClosingCount(): string | number {
        return this._serverData?.audit_totals?.audit?.closing_count ?? 0;
    }

    get auditOpeningValue(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.opening_value);
    }

    get auditClosingValue(): number {
        return this._parseNum(this._serverData?.audit_totals?.audit?.closing_value);
    }

    /**
     * Lazy-loaded moved-to-backstock items for the dedicated tab
     */
    get movedToBackstockItems(): MovedToBackstockItem[] {
        if (this._movedToBackstockItems) return this._movedToBackstockItems;

        const sourceItems = this._serverData?.moved_to_backstock;
        if (!Array.isArray(sourceItems)) {
            this._movedToBackstockItems = [];
            return this._movedToBackstockItems;
        }

        this._movedToBackstockItems = sourceItems.map((item: any, idx: number) => {
            const ticketNumberAtMove = this._parseNum(item?.ticket_number_at_move);
            const ticketsRemaining = this._parseNum(item?.tickets_remaining);
            const start = Number(ticketNumberAtMove % 1000);
            const endRaw = ticketsRemaining === 0 ? ticketNumberAtMove : (ticketNumberAtMove + ticketsRemaining);
            const end = Number(endRaw % 1000);
            const range = `${start.toString().padStart(3, '0')}-${end.toString().padStart(3, '0')}`;
            const costPerTicket = this._parseNum(item?.game_cost_per_ticket);

            return {
                id: `${item?.from_audit_id ?? 'from'}-${item?.to_audit_id ?? 'to'}-${item?.game_id ?? 'game'}-${item?.pack_id ?? idx}`,
                gameId: String(item?.game_id ?? ''),
                gameName: String(item?.game_name ?? ''),
                plu: String(item?.game_PLU ?? ''),
                costPerTicket,
                packId: String(item?.pack_id ?? ''),
                ticketNumberAtMove,
                ticketsRemaining,
                movedAt: item?.moved_at,
                fromAuditId: item?.from_audit_id,
                toAuditId: item?.to_audit_id,
                moved_to_backstock_range: range,
                moved_to_backstock_count: ticketsRemaining,
                moved_to_backstock_value: ticketsRemaining * costPerTicket
            };
        });

        return this._movedToBackstockItems;
    }

    /**
     * Lazy-loaded items for the table
     */
    get items(): AuditDetailItem[] {
        if (this._items) return this._items;

        this._items = [];
        if (!this._serverData?.games) return this._items;

        this._serverData.games.forEach((game: any) => {
            const packs = game.packs || [];
            
            // If game has packs, create items for each pack
            if (packs.length > 0) {
                packs.forEach((pack: any) => {
                    this._items!.push(new AuditDetailItem(
                        `${game.game_id}-${pack.pack_id}`,
                        game.game_id,
                        game.game_name,
                        String(game.plu),
                        game.cost_per_ticket,
                        String(pack.pack_id),
                        pack.starting_ticket_number,
                        pack.ending_ticket_number,
                        pack.qty_sold,
                        game.pos_totals?.scratcher_count ?? 0,
                        game.pos_totals?.scratcher_sales ?? 0,
                        game.reconciliation_variance?.quantity_variance ?? 0,
                        game.reconciliation_variance?.revenue_variance ?? 0,
                        pack.is_newly_activated,
                        pack.starting_ticket_display,
                        pack.ending_ticket_display,
                        Boolean(pack.is_manual_sold_out),
                        pack.validation_message
                    ));
                });
            } else {
                // If game has no packs (packs.length === 0), create one item with empty pack data
                this._items!.push(new AuditDetailItem(
                    `${game.game_id}-no-pack`,
                    game.game_id,
                    game.game_name,
                    String(game.plu),
                    game.cost_per_ticket,
                    '', // Empty packId
                    0,  // Empty startingTicket
                    0,  // Empty endingTicket
                    0,  // Empty qtySold
                    game.pos_totals?.scratcher_count ?? 0,
                    game.pos_totals?.scratcher_sales ?? 0,
                    game.reconciliation_variance?.quantity_variance ?? 0,
                    game.reconciliation_variance?.revenue_variance ?? 0,
                    false, // isNewlyActivated
                    undefined,
                    undefined,
                    false,
                    game.validation_message
                ));
            }
        });

        return this._items;
    }

    /**
     * summaryTotals provides Card 2 data for AuditSummary component
     */
    get summaryTotals() {
        return {
            audit: {
                total_sold: this.auditTotalSold,
                total_revenue: this.auditTotalRevenue,
                total_packs: this.auditTotalPacks,
                total_games: this.auditTotalGames,
                newly_activated_count: this.auditNewlyActivatedCount,
                newly_activated_value: this.auditNewlyActivatedValue,
                opening_count: this.auditOpeningCount,
                closing_count: this.auditClosingCount,
                opening_value: this.auditOpeningValue,
                closing_value: this.auditClosingValue
            },
            pos: {
                scratcher_count: this.posTotalScratcherCount,
                scratcher_sales: this.posTotalScratcherSales
            },
            reconciliation_variance: {
                scratcher: {
                    quantity_variance: this.scratcherCountVariance,
                    revenue_variance: this.scratcherSalesVariance
                }
            }
        };
    }

    static fromServerData(data: any): AuditDetailModel {
        return new AuditDetailModel(data);
    }
}
